<template>
  <!-- Table Container Card -->
  <b-card no-body>

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('CREATE_INDIVIDUAL_CUSTOMER')"
            :label="$t('Add')"
            size="md"
            @click="goToAddCustomerInformation"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="customerInfoRef"
      :items="customerList"
      responsive
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>

      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(dateCreated)="data">
        {{ toDateWithYear(data.value) }}
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">

        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >

            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="getUserPermission('MODIFY_INDIVIDUAL_CUSTOMER')"
              @click="goToEditCustomerInformation(data.item, $event.target, 'edit')"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">{{ $t('Modify') }}</span>
            </b-dropdown-item>
            <b-dropdown-item @click="onViewCustomer(data.item, $event.target)">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50">{{ $t('View') }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      :ok-title="$t('Cancel')"
      ok-only
      :cancel-title="$t('Cancel')"
      ok-variant="outline-secondary"
      centered
      size="lg"
      :title="$t('Customer Information')"
    >
      <b-tabs>
        <b-tab
          :title="$t('Personal Information')"
          active
        >
          <b-row class="match-height">
            <!-- Bank Verification Number -->
            <b-col md="6">
              <InputField
                id="bvn"
                v-model="customerInformation.bvn"
                :label="$t('BVN')"
                type="text"
                readonly
              />
            </b-col>
            <!-- Bank Verification Number -->
            <b-col md="6">
              <InputField
                id="nin"
                v-model="customerInformation.nin"
                :label="$t('NIN')"
                type="text"
                readonly
              />
            </b-col>
            <b-col md="6">
              <InputField
                id="title"
                v-model="customerInformation.title"
                :label="$t('Title')"
                type="text"
                readonly
              />
            </b-col>
            <!-- Last Name -->
            <b-col md="6">
              <InputField
                id="last-name"
                v-model="customerInformation.lastName"
                :label="$t('Last Name')"
                type="text"
                readonly
              />
            </b-col>
            <!-- Last Name -->

            <!-- First Name -->
            <b-col md="6">
              <InputField
                id="first-name"
                v-model="customerInformation.firstName"
                :label="$t('First Name')"
                type="text"
                readonly
              />
            </b-col>
            <!-- First Name -->

            <!-- Other Name -->
            <b-col md="6">
              <InputField
                id="other-names"
                v-model="customerInformation.otherNames"
                :label="$t('Other Names')"
                type="text"
                readonly
              />
            </b-col>
            <!-- Other Name -->

            <!-- Maiden Name -->
            <b-col md="6">
              <InputField
                id="maiden-name"
                v-model="customerInformation.maidenName"
                :label="$t('Maiden Name')"
                type="text"
                readonly
              />
            </b-col>
            <!-- Maiden Name -->

            <!-- Gender -->
            <b-col md="6">
              <InputField
                id="gender1"
                v-model="customerInformation.gender"
                :label="$t('Gender')"
                type="text"
                readonly
              />

            </b-col>
            <!-- Gender -->
            <!-- Marital Status -->
            <b-col md="6">
              <InputField
                id="marital-status"
                v-model="customerInformation.maritalStatus"
                :label="$t('Marital Status')"
                type="text"
                readonly
              />

            </b-col>
            <!-- Marital Status -->
            <b-col md="6">
              <InputField
                id="user-id"
                v-model="customerInformation.userID"
                :label="$t('User ID')"
                type="text"
                readonly
              />

            </b-col>

            <!-- Phone Number -->
            <b-col md="6">
              <InputField
                id="phone-number"
                v-model="customerInformation.phoneNumber"
                :label="$t('Phone Number')"
                type="number"
                readonly
              />
            </b-col>
            <!-- Phone Number -->

            <!-- Notification Phone Number -->
            <b-col md="6">
              <TextAreaField
                id="phone-number"
                v-model="customerInformation.notificationPhoneNumber"
                :label="$t('Notification Phone Number')"
                type="text"
                rows="2"
                readonly
              />
            </b-col>
            <!-- Notification Phone Number -->

            <!-- Email -->
            <b-col md="6">
              <InputField
                id="email"
                v-model="customerInformation.email"
                :label="$t('Email')"
                type="email"
                readonly
              />
            </b-col>
            <!--  Email -->

            <!-- Notification Email -->
            <b-col md="6">
              <TextAreaField
                id="email"
                v-model="customerInformation.notificationEmail"
                :label="$t('Notification Email')"
                rows="2"
                readonly
              />
            </b-col>
            <!-- Notification Email -->

            <!-- Date of Birth -->
            <b-col md="6">
              <InputField
                id="date-of-birth"
                v-model="customerInformation.dateOfBirth"
                :label="$t('Date of Birth')"
                type="text"
                readonly
              />
            </b-col>
            <!--  Date of Birth -->

            <!-- State -->
            <b-col md="6">
              <InputField
                id="state"
                v-model="customerInformation.state"
                :label="$t('State')"
                type="text"
                readonly
              />
            </b-col>
            <!--  State -->

            <!-- LGA -->
            <b-col md="6">
              <InputField
                id="lga"
                v-model="customerInformation.lga"
                :label="$t('LGA')"
                type="text"
                readonly
              />
            </b-col>
            <!--  LGA -->

            <!-- Home Town -->
            <b-col md="6">
              <InputField
                id="home-town"
                v-model="customerInformation.hometown"
                :label="$t('Home Town')"
                type="text"
                readonly
              />
            </b-col>
            <!-- Home Town -->

            <!-- Religion -->
            <b-col md="6">
              <InputField
                id="religion"
                v-model="customerInformation.religion"
                :label="$t('Religion')"
                type="text"
                readonly
              />
            </b-col>
            <!--  Religion -->

            <!-- Nationality -->
            <b-col md="6">
              <InputField
                id="nationality"
                v-model="customerInformation.nationality"
                :label="$t('Nationality')"
                type="text"
                readonly
              />
            </b-col>
            <!-- Nationality -->

            <!--  Means of Identification -->
            <b-col md="6">
              <InputField
                id="means-of-id"
                v-model="customerInformation.meansOfIdentification"
                :value="customerInformation.meansOfIdentification"
                :label="$t('Means of Identification')"
                type="text"
                readonly
              />
            </b-col>
            <!--  Means of Identification -->

            <!-- Nationality -->
            <b-col md="6">
              <InputField
                id="id-number"
                v-model="customerInformation.idNumber"
                :label="$t('Means of Identification Number')"
                type="text"
                readonly
              />
            </b-col>
            <!-- Nationality -->
            <b-col md="6">
              <InputField
                id="issue-date"
                v-model="customerInformation.meansOfIdentificationIssueDate"
                :label="$t('Means of Identification Issue Date')"
                type="text"
                readonly
              />
            </b-col>
            <b-col md="6">
              <InputField
                id="expiry-date"
                v-model="customerInformation.meansOfIdentificationExpiryDate"
                :label="$t('Means of Identification Expiry Date')"
                type="text"
                readonly
              />
            </b-col>
            <b-col md="6">
              <InputField
                id="issuing-authority"
                v-model="customerInformation.meansOfIdentificationPlaceOfIssue"
                :label="$t('Issuing Authority')"
                :placeholder="$t('Enter Issuing Authority')"
                type="text"
                readonly
              />
            </b-col>

            <!-- PEP -->
            <b-col md="6">
              <InputField
                id="pep"
                v-model="customerInformation.pep"
                :label="$t('PEP')"
                type="text"
                for="pep"
                readonly
              />
            </b-col>
            <!-- PEP -->
            <!-- TIN -->
            <b-col md="6">
              <InputField
                id="tin"
                v-model="customerInformation.tin"
                :label="$t('TIN')"
                type="text"
                readonly
              />
            </b-col>
            <!-- TIN -->

            <!--  Old customer ID -->
            <b-col md="6">
              <InputField
                id="reference-number"
                v-model="customerInformation.oldCustomerID"
                :label="$t('Old customer ID')"
                type="text"
                show-icon
                :on-click="onViewInfo"
                readonly
              />
            </b-col>
            <!--  Old customer ID -->

            <!--  IPPIS/Oracle ID -->
            <b-col md="6">
              <InputField
                id="ippis-oracle"
                v-model="customerInformation.employeeID"
                :label="$t('IPPIS/Oracle ID')"
                :placeholder="$t('Enter IPPIS/Oracle ID')"
                type="text"
                readonly
              />
            </b-col>
            <!--  IPPIS/Oracle ID -->
            <!--  Address -->

            <b-col md="12">
              <TextAreaField
                id="address"
                v-model="customerInformation.address"
                :value="customerInformation.address"
                :label="$t('Address')"
                for="address"
                rows="5"
                readonly
              />
            </b-col>
          </b-row>
          <!--  Address -->
        </b-tab>
        <b-tab :title="$t('Next of Kin')">
          <b-row class="match-height">
            <!-- Name -->
            <b-col md="4">
              <InputField
                id="name"
                v-model="customerInformation.nokName"
                :label="$t('Name')"
                type="text"
                for="name"
                readonly
              />
            </b-col>
            <!-- Name -->

            <!-- Email -->
            <b-col md="4">

              <InputField
                id="email"
                v-model="customerInformation.nokEmail"
                :label="$t('Email')"
                type="email"
                for="email"
                readonly
              />

            </b-col>
            <!--  Email -->

            <!-- Phone Number -->
            <b-col md="4">

              <InputField
                id="phone-number"
                v-model="customerInformation.nokPhoneNumber"
                :label="$t('Phone Number')"
                type="number"
                for="phone-number"
                readonly
              />

            </b-col>
            <!-- Phone Number -->
          </b-row>

          <b-row class="match-height">
            <!-- Relationship -->
            <b-col md="4">
              <InputField
                id="relationship"
                v-model="customerInformation.nokRelationship"
                :label="$t('Relationship')"
                type="text"
                for="relationship"
                readonly
              />
            </b-col>
            <!-- Relationship -->

            <!--Gender -->
            <b-col md="4">
              <InputField
                id="gender"
                v-model="customerInformation.nokGender"
                :label="$t('Gender')"
                type="text"
                for="gender"
                readonly
              />
            </b-col>
            <!-- Gender -->
          </b-row>

          <!--  Address -->
          <b-row class="match-height">
            <b-col md="12">
              <TextAreaField
                id="next-of-kin-address"
                v-model="customerInformation.nokAddress "
                :value="customerInformation.nokAddress"
                :label="$t('Address')"
                for="next-of-kin-address"
                rows="5"
                readonly
              />
            </b-col>
          </b-row>
          <!--  Address -->
        </b-tab>

        <b-tab :title="$t('Work Information')">
          <b-row class="match-height">
            <!-- Occupation -->
            <b-col md="4">
              <InputField
                id="occupation"
                v-model="customerInformation.occupation"
                :label="$t('Name')"
                type="text"
                for="occupation"
                readonly
              />
            </b-col>
            <!-- Occupation -->

            <!-- Passport -->
            <b-col md="4">

              <InputField
                id="employer-name"
                v-model="customerInformation.employerName"
                :label="$t('Employer\'s Name')"
                type="text"
                for="employer-name"
                readonly
              />

            </b-col>
            <!--  Email -->

            <!-- Phone Number -->
            <b-col md="4">

              <InputField
                id="phone-number"
                v-model="customerInformation.officePhoneNumber"
                :label="$t('Phone Number')"
                type="number"
                for="phone-number"
                readonly
              />

            </b-col>
            <!-- Phone Number -->
          </b-row>

          <!--  Address -->
          <b-row class="match-height">
            <b-col md="12">
              <TextAreaField
                id="employer-address"
                v-model="customerInformation.employerAddress"
                :value="customerInformation.employerAddress"
                :label="$t('Employer\'s Address')"
                for="employer-address"
                rows="5"
                readonly
              />
            </b-col>
          </b-row>
          <!--  Address -->
        </b-tab>

        <b-tab :title="$t('Other Documents')">
          <!-- <b-row class="match-height"> -->
          <b-form>
            <b-row
              v-for="(item, index) in documentsFormData"
              :id="index"
              :key="index"
              class="mb-2 match-height"
            >
              <b-col md="6">
                <SelectField
                  id="document-type"
                  v-model="item.type"
                  :reduce="option => option.value"
                  :label="$t('Document Type')"
                  :placeholder="$t('Select')"
                  :options="translatedDocumentTypeOptions"
                  item="label"
                  readonly
                />
              </b-col>
              <b-col
                md="7"
              >
                <label>{{ $t('Upload Documents') }}</label>

                <div class="upload-image">
                  <b-form-group>
                    <b-form-file
                      :placeholder="$t('Choose a file or drop it here...')"
                      :drop-placeholder="$t('Drop file here...')"
                      accept="image/jpeg, image/bmp, image/png, image/jpg, application/pdf, .doc, .docx, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      plain
                      disabled
                      @change="onDocumentsUpload(index, $event)"
                    />
                    <div
                      v-if="!item.url"
                      class="upload-instruction"
                    >
                      <h4>{{ $t('Upload Document File') }}</h4>
                      <p>{{ $t('Drag & drop file or choose from your device to upload') }}</p>
                      <p class="format">
                        {{ $t('Format accepted (jpg, png, bmp, jpeg, docx, pdf)') }}
                      </p>
                    </div>
                    <div v-else>
                      <div v-if="item.fileExtension === 'png'|| item.fileExtension === 'jpeg'|| item.fileExtension === 'jpg'|| item.fileExtension === 'bmp'">
                        <b-img
                          :src="item.url"
                          block
                          rounded
                          width="250px"
                          height="250px"
                        />
                      </div>
                      <div
                        v-else-if="item.fileExtension === 'pdf'"
                      >
                        <b-img
                          :src="pdfIcon"
                          block
                          rounded
                          width="250px"
                          height="250px"
                        />
                      </div>
                      <div
                        v-else-if="item.fileExtension === 'vnd'"
                      >
                        <b-img
                          :src="wordIcon"
                          block
                          rounded
                          width="250px"
                          height="250px"
                        />
                      </div>
                      <div
                        v-else
                      >
                        <b-img
                          src=""
                        />
                      </div>
                      <div
                        v-if="item.url"
                        class="view-details"
                      >
                        <feather-icon
                          icon="EyeIcon"
                          size="20"
                          class="text-primary"
                          @click="viewDocumentFile(item.url, item.fileName, item.fileExtension)"
                        />
                      </div>
                    </div>
                  </b-form-group>
                </div>
              </b-col>
              <b-col
                v-if="item.url"
                md="3"
                class="mt-0"
              >
                <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="primary"
                  class="ml-0"
                  @click="handleDownload(item.url)"
                >
                  <feather-icon
                    icon="DownloadIcon"
                    class="mr-25"
                  />
                  <span>{{ $t('Download') }}</span>
                </b-button>
              </b-col>
            </b-row>
          </b-form>
        </b-tab>
      </b-tabs>
    </b-modal>
    <!-- modal -->

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      no-close-on-backdrop
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchCustomerInformation"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Customer ID -->
        <b-col md="6">
          <InputField
            id="customer-id"
            v-model="filter.customerID"
            :label="$t('Customer ID')"
            :placeholder="$t('Enter customer ID')"
            type="text"
          />
        </b-col>
        <!-- Customer ID -->

        <!-- Reference ID -->
        <b-col md="6">
          <InputField
            id="reference-id"
            v-model="filter.oldCustomerID"
            :label="$t('Reference ID')"
            :placeholder="$t('Enter Reference ID')"
            type="text"
            for="reference-id"
          />
        </b-col>
        <!--Reference ID -->

        <!-- Name -->
        <b-col md="6">
          <InputField
            id="name"
            v-model="filter.name"
            :label="$t('Customer Name')"
            :placeholder="$t('Enter name')"
            type="text"
          />
        </b-col>
        <!-- Name -->

        <!-- PhoneNumber -->
        <b-col md="6">
          <InputField
            id="phone-number"
            v-model="filter.phoneNumber"
            :label="$t('Phone Number')"
            :placeholder="$t('Enter phone number')"
            type="number"
          />
        </b-col>
        <!-- PhoneNumber -->

        <!-- Email -->
        <b-col md="6">
          <InputField
            id="email"
            v-model="filter.email"
            :label="$t('Email')"
            :placeholder="$t('Enter email')"
            type="email"
          />
        </b-col>
        <!-- Email -->
        <!-- BVN -->
        <b-col md="6">
          <InputField
            id="bvn"
            v-model="filter.bvn"
            :label="$t('BVN')"
            :placeholder="$t('Enter BVN')"
            type="text"
            for="bvn"
          />
        </b-col>
        <!--BVN -->

        <!-- NIN -->
        <b-col md="6">
          <InputField
            id="nin"
            v-model="filter.nin"
            :label="$t('NIN')"
            :placeholder="$t('Enter NIN')"
            type="text"
            for="nin"
          />
        </b-col>
        <!--NIN -->

        <!-- IPPIS/Oracle ID -->
        <b-col md="6">
          <InputField
            id="ippis-oracle-id"
            v-model="filter.employeeID"
            :label="$t('IPPIS/Oracle ID')"
            :placeholder="$t('Enter IPPIS/Oracle ID')"
            type="text"
            for="ippis-oracle-id"
          />
        </b-col>
        <!--IPPIS/Oracle ID -->
      </b-row>
    </b-modal>
    <!-- Filter modal -->

    <b-modal
      id="info-modal"
      ref="infoModal"
      centered
      size="md"
      ok-only
      :ok-title="$t('Okay')"
      @ok="hideModal('info-modal')"
    >
      <div class="p-2">
        <p>{{ $t('External Customer Reference') }}</p>
      </div>
    </b-modal>
    <b-modal
      id="view-avatar-modal"
      ref="avatarModal"
      no-close-on-backdrop
      :ok-title="$t('Download File')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="viewDocumentModalTitle"
      @hidden="resetDocumentModal"
      @cancel="resetDocumentModal"
      @ok="handleDownload(viewDocumentData)"
    >
      <div class="m-2 text-center">
        <b-row>
          <b-col cols="12">
            <div v-if="fileExtension === 'png'|| fileExtension === 'jpeg'|| fileExtension === 'jpg' || fileExtension === 'bmp'">
              <b-img
                :src="viewDocumentData"
                block
                fluid
                rounded
              />
            </div>
            <div v-else-if="fileExtension === 'pdf'">
              <b-img
                :src="pdfIcon"
                block
                width="250px"
                height="250px"
                rounded
              />
            </div>
            <div v-else-if="fileExtension === 'vnd'">
              <b-img
                :src="wordIcon"
                block
                width="250px"
                height="250px"
                rounded
              />
            </div>
            <div v-else>
              <b-img
                src=""
              /></div>
          </b-col>
        </b-row>
      </div>
    </b-modal>

    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BTable,
  BTabs,
  BTab,
  BImg,
  BButton,
  BDropdown,
  BDropdownItem,
  BModal,
  BSpinner,
  BFormGroup, BFormFile, BForm,
} from 'bootstrap-vue'
import { mapActions } from 'vuex'
import { required } from '@validations'
import Ripple from 'vue-ripple-directive'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import InputField from '../../../../forms/InputField.vue'
import SelectField from '../../../../forms/SelectField.vue'
import TextAreaField from '../../../../forms/TextAreaField.vue'
import { toDateWithYear } from '../../../../../helpers/formatDate'
import { gender, pdfIcon, wordIcon } from '../../../../../helpers/data'
import Button from '../../../../components/button/Button.vue'
import { accountPermissions } from '../../../../../helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BSpinner,
    BTable,
    BDropdown,
    BDropdownItem,
    BImg,
    Button,
    BModal,
    BTabs,
    BTab,
    BButton,
    InputField,
    SelectField,
    TextAreaField,
    FullScreenSpinner,
    TablePagination,
    TableEntries,
    BFormGroup,
    BFormFile,
    BForm,
  },
  directives: {
    Ripple,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      toDateWithYear,
      isBusy: true,
      showSpinner: false,
      required,
      genderOptions: gender,
      pdfIcon,
      wordIcon,
      documentsFormData: [{
        type: '',
        url: '',
        file: '',
      }],
      viewDocumentData: '',
      viewDocumentModalTitle: '',
      fileExtension: '',
      documentTypeOptions: [
        { value: 1, label: 'Signature Specimen' },
        { value: 2, label: 'Proof Of Address' },
        { value: 3, label: 'Driver’s License' },
        { value: 4, label: 'National Identity Card' },
        { value: 5, label: 'Voters Card' },
        { value: 6, label: 'National Identity Number' },
        { value: 7, label: 'International Passport' },
        { value: 8, label: 'Passport Photograph' },
        { value: 9, label: 'Reference Letter' },
        { value: 10, label: 'Guarantor’s Letter' },
        { value: 11, label: 'Loan Application Form' },
        { value: 12, label: 'Credit Bureau Form' },
        { value: 13, label: 'Others' },
      ],
      documentIdentifier: '',
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'fullName', sortable: true, label: 'Full Name' },
        { key: 'customerID', sortable: true, label: 'Customer ID' },
        { key: 'phoneNumber', sortable: true, label: 'Phone Number' },
        { key: 'employeeID', sortable: true, label: 'IPPIS/Oracle ID' },
        { key: 'email', sortable: true, label: 'Email' },
        { key: 'state', sortable: true, label: 'State' },
        { key: 'oldCustomerID', sortable: true, label: 'Reference ID' },
        { key: 'dateCreated', sortable: true, label: 'Date Created' },
        { key: 'actions' },
      ],
      categoryFormData: {
        categoryName: '',
        level: 'Select',
        specifyLevel: 'Select',
      },
      levelOptions: [
        { label: 'Asset', value: 1 },
        { label: 'Liability', value: 2 },
        { label: 'Capital', value: 3 },
        { label: 'Income', value: 4 },
        { label: 'Expense', value: 5 },
      ],
      specifylevelOptions: [
        { label: 'Level 2', value: 1 },
        { label: 'Level 3', value: 2 },
      ],
      customerList: [],
      customerCache: {}, // { [cacheKey]: { data: [], totalCount: number } }
      cacheStale: false,
      customerInformation: {
        customerID: '',
        title: '',
        lastName: '',
        firstName: '',
        otherNames: '',
        address: '',
        gender: '',
        maritalStatus: '',
        userID: '',
        maidenName: '',
        religion: '',
        phoneNumber: '',
        notificationPhoneNumber: '',
        email: '',
        notificationEmail: '',
        hometown: '',
        dateOfBirth: '',
        nationality: '',
        state: '',
        lga: '',
        bvn: '',
        nin: '',
        meansOfIdentification: '',
        idNumber: '',
        meansOfIdentificationIssueDate: null,
        meansOfIdentificationExpiryDate: null,
        meansOfIdentificationPlaceOfIssue: '',
        pep: '',
        tin: '',
        nokName: '',
        nokAddress: '',
        nokGender: '',
        nokRelationship: '',
        nokPhoneNumber: '',
        nokEmail: '',
        occupation: '',
        employerName: '',
        employerAddress: '',
        officePhoneNumber: '',
        employeeID: '',
      },
      filter: {
        customerID: '',
        oldCustomerID: '',
        employeeID: '',
        customerType: '',
        lastName: '',
        gender: '',
        phoneNumber: '',
        email: '',
        sortColumn: '',
        direction: '',
        bvn: '',
        nin: '',
      },
    }
  },
  computed: {
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedDocumentTypeOptions() {
      return this.documentTypeOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchCustomerInformation()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchCustomerInformation()
    },
  },
  mounted() {
    this.fetchCustomerInformation()
  },
  methods: {
    ...mapActions({
      getIndividualCustomer: 'account/getCustomerList',
      getIndividualCustomerByID: 'account/getCustomer',
    }),
    handleDownload(data) {
      const link = document.createElement('a')
      link.href = data
      link.click()
      this.alertSuccess({ text: this.$t('Your request to download file was successful') })
    },
    onViewInfo() {
      this.$root.$emit('bv::show::modal', 'info-modal')
    },
    viewDocumentFile(image, title, fileExtension) {
      this.fileExtension = fileExtension
      this.viewDocumentData = image
      this.viewDocumentModalTitle = title.replace(/([a-zA-Z])(?=[A-Z])/g, '$1 ')
      this.$root.$emit('bv::show::modal', 'view-avatar-modal')
    },
    resetDocumentModal() {
      this.viewDocumentData = ''
      this.viewDocumentModalTitle = ''
    },
    async onViewCustomer(items) {
      const { id } = items
      try {
        this.showSpinner = true
        const response = await this.getIndividualCustomerByID({ id, permission: accountPermissions.VIEW_INDIVIDUAL_CUSTOMER })
        const { data, status } = response
        if (status === 200 && data.data) {
          this.customerInformation = {
            ...data.data,
            dateOfBirth: items.dateOfBirth ? toDateWithYear(items.dateOfBirth) : null,
            meansOfIdentificationIssueDate: items.meansOfIdentificationIssueDate ? toDateWithYear(items.meansOfIdentificationIssueDate) : null,
            meansOfIdentificationExpiryDate: items.meansOfIdentificationExpiryDate ? toDateWithYear(items.meansOfIdentificationExpiryDate) : null,
          }
          this.documentIdentifier = data.data.documentIdentifier
          this.documentsFormData = data.data.documents ? data.data.documents.map(item => ({
            url: item.url,
            type: item.typeID,
            id: item.id,
            fileName: item.type,
            fileExtension: item.fileExtension,
          })) : [{
            url: '',
            type: '',
          }]
          this.$root.$emit('bv::show::modal', 'modal-lg')
          this.showSpinner = false
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.showSpinner = false
      }
    },
    async fetchCustomerInformation() {
      this.isBusy = true
      this.hideModal('modal-filter')
      try {
        this.filter.customerType = 'Individual'
        const cacheKey = JSON.stringify({ page: this.currentPage, perPage: this.perPage, filter: this.filter })
        if (!this.cacheStale && this.customerCache[cacheKey]) {
          const cached = this.customerCache[cacheKey]
          this.customerList = cached.data
          this.totalCount = cached.totalCount
          this.isBusy = false
          return
        }
        const response = await this.getIndividualCustomer({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: accountPermissions.VIEW_INDIVIDUAL_CUSTOMER,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          const mappedData = data.data.map(item => ({
            ...item,
            fullName: `${item?.lastName ?? ''} ${item?.firstName ?? ''} ${item?.otherNames ?? ''}`,
          }))
          this.customerList = mappedData
          this.totalCount = data.recordCount
          this.customerCache[cacheKey] = {
            data: mappedData,
            totalCount: data.recordCount,
          }
        } else {
          this.customerList = []
          this.totalCount = data.recordCount
        }
        this.cacheStale = false
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.cacheStale = true
      this.fetchCustomerInformation()
    },
    goToAddCustomerInformation() {
      this.$router
        .push({
          name: 'individual-customer',
          params: {
            type: 'add',
          },
        })
        .catch(error => {
          this.alertError({ text: error })
        })
    },
    goToEditCustomerInformation(item, activity, val) {
      this.$router
        .push({
          name: 'individual-customer',
          params: {
            type: val,
            id: item.id,
          },
        })
        .catch(error => {
          this.alertError({ text: error })
        })
    },
  },
}
</script>
<style>
.nav-tabs .nav-link:after {
    background:  var(--primary) !important;
  }
</style>
<style scoped>
.modal-body {
  position: relative;
  flex: 1 1 auto;
  max-height: calc(80vh - 140px) !important;
}
.upload-image{
    border: 2px dashed #DCD9D9;
    position: relative;
    width: 250px;
    height: 250px
}
.format {
    color: #999999 !important;
}
form input[type=file]{
  position: absolute;
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
  outline: none;
  opacity: 0;
}
.upload-instruction{
  padding-top: 22%;
  color: #000;
  text-align: center;
}
.view-details {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  background: #fff;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 10px;
  margin-left: 10px;
  cursor: pointer;
}
.upload {
  max-width: 100%;
  max-height: 100%;
}
</style>
